import { Link } from 'react-router-dom'
import { useCampaigns } from '@/hooks/useCampaigns'
import { Megaphone, TrendingUp, Users, BarChart3, ExternalLink } from 'lucide-react'

export default function CampaignsPage() {
  const { data: campaigns, isLoading } = useCampaigns()

  const maturityColor = (level: string) => {
    switch (level) {
      case 'avancado': return 'text-emerald-500'
      case 'intermediario': return 'text-amber-500'
      case 'inicial': return 'text-red-500'
      default: return 'text-muted-foreground'
    }
  }

  const maturityLabel = (level: string) => {
    switch (level) {
      case 'avancado': return 'Avançado'
      case 'intermediario': return 'Intermediário'
      case 'inicial': return 'Inicial'
      default: return level
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Campanhas</h1>
          <p className="text-muted-foreground mt-1">Diagnósticos ESG e captação de leads</p>
        </div>
        <a
          href="/diagnostico-esg/"
          target="_blank"
          rel="noopener"
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          Ver Landing Page
        </a>
      </div>

      {campaigns && campaigns.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-card border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/10 rounded-lg"><Megaphone className="w-5 h-5 text-emerald-500" /></div>
              <div>
                <p className="text-2xl font-bold">{campaigns.length}</p>
                <p className="text-xs text-muted-foreground">Campanhas</p>
              </div>
            </div>
          </div>
          <div className="bg-card border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg"><Users className="w-5 h-5 text-blue-500" /></div>
              <div>
                <p className="text-2xl font-bold">{campaigns.reduce((a, c) => a + c.total_submissions, 0)}</p>
                <p className="text-xs text-muted-foreground">Total Submissões</p>
              </div>
            </div>
          </div>
          <div className="bg-card border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/10 rounded-lg"><TrendingUp className="w-5 h-5 text-amber-500" /></div>
              <div>
                <p className="text-2xl font-bold">
                  {(() => {
                    const scores = campaigns.filter(c => c.avg_score !== null).map(c => c.avg_score!)
                    return scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(0) + '%' : '—'
                  })()}
                </p>
                <p className="text-xs text-muted-foreground">Score Médio</p>
              </div>
            </div>
          </div>
          <div className="bg-card border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg"><BarChart3 className="w-5 h-5 text-purple-500" /></div>
              <div>
                <p className="text-2xl font-bold">
                  {campaigns.filter(c => c.is_active).length}
                </p>
                <p className="text-xs text-muted-foreground">Ativas</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /></div>
      ) : !campaigns || campaigns.length === 0 ? (
        <div className="bg-card border rounded-lg p-12 text-center">
          <Megaphone className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold">Nenhuma campanha</h3>
          <p className="text-muted-foreground mt-1">Crie uma campanha para começar a captar leads.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign) => (
            <Link
              key={campaign.id}
              to={`/campaigns/${campaign.id}`}
              className="block bg-card border rounded-lg p-5 hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold">{campaign.name}</h3>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${campaign.is_active ? 'bg-emerald-500/10 text-emerald-500' : 'bg-gray-500/10 text-gray-500'}`}>
                      {campaign.is_active ? 'Ativa' : 'Inativa'}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-500">
                      {campaign.campaign_type.replace('_', ' ')}
                    </span>
                  </div>
                  {campaign.description && (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{campaign.description}</p>
                  )}
                </div>
                <div className="flex gap-6 text-right ml-4">
                  <div>
                    <p className="text-2xl font-bold">{campaign.total_submissions}</p>
                    <p className="text-xs text-muted-foreground">Submissões</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{campaign.avg_score !== null ? campaign.avg_score.toFixed(0) + '%' : '—'}</p>
                    <p className="text-xs text-muted-foreground">Score Médio</p>
                  </div>
                </div>
              </div>
              {Object.keys(campaign.maturity_distribution).length > 0 && (
                <div className="mt-4">
                  <div className="flex gap-4 text-xs">
                    {Object.entries(campaign.maturity_distribution).map(([level, count]) => (
                      <span key={level} className={maturityColor(level)}>
                        {maturityLabel(level)}: {count}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
